import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAppSelector } from '../../../state/store';
import {
  Button,
  Input,
  Dropdown,
  COLORS,
  Toaster,
  Tag,
  BodySecondary,
} from '@bikdotai/bik-component-library';
import MultiSelectTeamAgentPopup, { TeamI, UserI } from './MultiSelectTeamAgentPopup';
import EditMacroModalShimmer from './EditMacroModalShimmer';
import Modal from '../../modal';
import { CrmService } from '../../../admin/services/CrmService';
import { Macro, CreateOrUpdateMacroRequest } from '../macroNode.types';
import CrossIcon from '../../../icons/crossIcon';
import PlusIcon from '../../../icons/Plus';
import InfoIcon from '../../../icons/infoIcon';
import {
  ModalOuterWrapper,
  ModalHeader,
  ModalHeaderTitle,
  ModalContent,
  ModalFormContainer,
  FormSection,
  DropdownContainer,
  ModalFooter,
  TeamsAgentsSection,
  ChipsContainer,
  Backdrop,
} from './EditMacroModal.style';

interface EditMacroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  macro: Macro;
  storeId: string;
}

export enum VisibilityOption {
  ALL_AGENTS_TEAMS = 'all_agents_teams',
  SPECIFIC_TEAMS = 'specific_teams',
}

const VISIBILITY_OPTIONS = [
  { label: 'All agents and teams', value: VisibilityOption.ALL_AGENTS_TEAMS },
  { label: 'Only specific teams or agents', value: VisibilityOption.SPECIFIC_TEAMS },
];

const EditMacroModal: React.FC<EditMacroModalProps> = ({
  isOpen,
  onClose,
  onSave,
  macro,
  storeId,
}) => {
  const [macroName, setMacroName] = useState(macro.title || '');
  const [description, setDescription] = useState(macro.description || '');
  const [visibility, setVisibility] = useState<VisibilityOption>(
    macro.visibleToTeams?.length || macro.visibleToAgents?.length
      ? VisibilityOption.SPECIFIC_TEAMS
      : VisibilityOption.ALL_AGENTS_TEAMS
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>(
    macro.visibleToTeams || []
  );
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>(
    macro.visibleToAgents || []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [teamList, setTeamList] = useState<TeamI[]>([]);
  const [agentList, setAgentList] = useState<UserI[]>([]);
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState('');
  const [toasterType, setToasterType] = useState<'success' | 'error'>('success');
  const [showAgentTeamSelector, setShowAgentTeamSelector] = useState(false);
  const [showVisibilityError, setShowVisibilityError] = useState(false);
  const addButtonRef = useRef<HTMLDivElement>(null);

  const crmService = useMemo(() => new CrmService(), []);
  const homeState = useAppSelector((state: any) => state.homeState);

  // Fetch teams and agents on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsDataLoading(true);
      try {
        const teamsResponse: any = await crmService.getTeams(storeId);
        if (teamsResponse?.data?.teams) {
          setTeamList(teamsResponse.data.teams);
        }

        const usersResponse: any = await crmService.getUsers(storeId, true);
        if (usersResponse?.data?.users) {
          setAgentList(usersResponse.data.users);
        }
      } catch (error) {
        console.error('Error fetching teams/agents:', error);
      } finally {
        setIsDataLoading(false);
      }
    };

    if (isOpen && storeId) {
      fetchData();
    }
  }, [isOpen, storeId, crmService]);

  // Sync form state when macro prop changes
  useEffect(() => {
    if (macro && macro.macroId) {
      setMacroName(macro.title || '');
      setDescription(macro.description || '');

      const hasVisibility =
        macro.visibleToTeams?.length || macro.visibleToAgents?.length;
      setVisibility(hasVisibility ? VisibilityOption.SPECIFIC_TEAMS : VisibilityOption.ALL_AGENTS_TEAMS);

      setSelectedTeamIds(macro.visibleToTeams || []);
      setSelectedAgentIds(macro.visibleToAgents || []);
    }
  }, [macro]);

  // Form validation
  const isFormValid = useMemo(() => {
    return (
      macroName.trim().length > 0 &&
      macroName.trim().length <= 50 &&
      description.trim().length > 0 &&
      description.trim().length <= 200
    );
  }, [macroName, description]);

  const handleTeamAgentChange = (teamIds: number[], agentIds: number[]) => {
    setSelectedTeamIds(teamIds);
    setSelectedAgentIds(agentIds);
    setShowAgentTeamSelector(false);
    if (teamIds.length > 0 || agentIds.length > 0) {
      setShowVisibilityError(false);
    }
  };

  const handleRemoveTeam = (teamId: number) => {
    setSelectedTeamIds(prev => prev.filter(id => id !== teamId));
  };

  const handleRemoveAgent = (agentId: number) => {
    setSelectedAgentIds(prev => prev.filter(id => id !== agentId));
  };

  // Get team and agent names from IDs
  const selectedTeamInfo = useMemo(() => {
    return selectedTeamIds
      .map(id => teamList.find(t => t.id === id))
      .filter(Boolean) as TeamI[];
  }, [selectedTeamIds, teamList]);

  const selectedAgentInfo = useMemo(() => {
    return selectedAgentIds
      .map(id => agentList.find(a => a.id === id))
      .filter(Boolean) as UserI[];
  }, [selectedAgentIds, agentList]);

  const handleSave = async () => {
    if (!isFormValid) return;

    if (visibility === VisibilityOption.SPECIFIC_TEAMS && selectedTeamIds.length === 0 && selectedAgentIds.length === 0) {
      setShowVisibilityError(true);
      return;
    }

    setIsLoading(true);
    try {
      const payload: CreateOrUpdateMacroRequest = {
        storeId,
        macroId: macro.macroId,
        title: macroName.trim(),
        description: description.trim(),
        channels: macro.channels,
        visibleToTeams: visibility === VisibilityOption.SPECIFIC_TEAMS ? selectedTeamIds : [],
        visibleToAgents: visibility === VisibilityOption.SPECIFIC_TEAMS ? selectedAgentIds : [],
        status: macro.status,
        isResponseIncluded: macro.isResponseIncluded,
        isAgentReviewRequired: macro.isAgentReviewRequired,
        config: macro.config,
        index: macro.index,
        updatedBy: homeState?.userData?.agentId || 537,
      };

      const response: any = await crmService.createOrUpdateMacro(payload);

      if (response?.status === 200 || response?.data?.success) {
        onSave();
        onClose();
      } else {
        throw new Error('Failed to update macro');
      }
    } catch (error: any) {
      console.error('Error saving macro:', error);
      setToasterMessage(error?.message || 'Failed to update macro');
      setToasterType('error');
      setShowToaster(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVisibilityChange = (option: any) => {
    setVisibility(option.value);
    setShowVisibilityError(false);
    if (option.value === VisibilityOption.ALL_AGENTS_TEAMS) {
      setSelectedTeamIds([]);
      setSelectedAgentIds([]);
    }
  };

  return (
    <>
      {isOpen && (
        <Modal
          onBackdropPress={onClose}
          childrenStyle={{
            width: '720px',
            maxHeight: '90vh',
            maxWidth: '90vw',
          }}
        >
          <ModalOuterWrapper>
          <ModalHeader>
            <ModalHeaderTitle>Edit quick reply</ModalHeaderTitle>
            {/* <BodySecondary color={COLORS?.content?.secondary || '#9E9E9E'}>
              Add labels to get context on topics discussed with the customer
            </BodySecondary> */}
          </ModalHeader>
          <ModalContent>
            {isDataLoading ? (
              <EditMacroModalShimmer />
            ) : (
              <ModalFormContainer>
                {/* Macro Name Field */}
                <FormSection>
                  <Input
                    labelText="Short code"
                    isRequired
                    placeholder="eg: Refund process query"
                    value={macroName}
                    maxCharLimit={50}
                    onChangeText={(text) => setMacroName(text.slice(0, 50))}
                    hintText="Display name seen by agents"
                  />
                </FormSection>

                {/* Description Field */}
                <FormSection>
                  <Input
                    labelText="Description"
                    isRequired
                    type="multiline"
                    placeholder="eg: Use this when a customer asks about refund process and policies."
                    value={description}
                    maxCharLimit={200}
                    onChangeText={(text) => setDescription(text.slice(0, 200))}
                    hintText="Details for agents to understand what this quick reply is for."
                  />
                </FormSection>

                {/* Visibility Field */}
                <FormSection>
                  <BodySecondary>Visibility</BodySecondary>
                  <DropdownContainer>
                    <Dropdown
                      placement='bottom-start'
                      options={VISIBILITY_OPTIONS.map((opt) => ({
                        ...opt,
                        selected: opt.value === visibility,
                      }))}
                      onSelect={handleVisibilityChange}
                      placeHolder="Select visibility"
                      truncatedText={false}
                    />
                  </DropdownContainer>

                  {/* Teams/Agents Selection with Chips */}
                  {visibility === VisibilityOption.SPECIFIC_TEAMS && (
                    <TeamsAgentsSection>
                      {showVisibilityError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: COLORS.content.negative, fontSize: 12, marginBottom: 4 }}>
                          <InfoIcon width={14} height={14} color={COLORS.content.negative} />
                          Please add at least one team or agent
                        </div>
                      )}
                      <ChipsContainer>
                        {selectedTeamInfo.map(team => (
                          <Tag
                            key={`team-${team.id}`}
                            version="2.0"
                            TrailingIcon={CrossIcon}
                            type="neutral"
                            tagText={team.name}
                            onTrailingIconClicked={() => handleRemoveTeam(team.id)}
                            cursor="pointer"
                          />
                        ))}
                        {selectedAgentInfo.map(agent => (
                          <Tag
                            key={`agent-${agent.id}`}
                            version="2.0"
                            TrailingIcon={CrossIcon}
                            type="neutral"
                            tagText={agent.name}
                            onTrailingIconClicked={() => handleRemoveAgent(agent.id)}
                            cursor="pointer"
                          />
                        ))}
                        <div
                          ref={addButtonRef}
                          onClick={() => setShowAgentTeamSelector(prev => !prev)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Tag
                            version="2.0"
                            type="purple"
                            tagText="Add agents/teams"
                            LeadingIcon={PlusIcon}
                          />
                        </div>
                      </ChipsContainer>

                      {/* Multi-Select Team/Agent Selector Popup */}
                      {showAgentTeamSelector && (
                        <>
                          <Backdrop onClick={() => setShowAgentTeamSelector(false)} />
                          <MultiSelectTeamAgentPopup
                            selectedTeamIds={selectedTeamIds}
                            selectedAgentIds={selectedAgentIds}
                            allTeams={teamList}
                            allUsers={agentList}
                            onApply={handleTeamAgentChange}
                            onClose={() => setShowAgentTeamSelector(false)}
                            triggerRef={addButtonRef}
                          />
                        </>
                      )}
                    </TeamsAgentsSection>
                  )}
                </FormSection>
              </ModalFormContainer>
            )}
          </ModalContent>

          {/* Action Buttons */}
          <ModalFooter>
            <Button
              buttonText="Cancel"
              buttonType="secondary"
              onClick={onClose}
              disabled={isLoading}
              size="small"
            />
            <Button
              buttonText="Update"
              buttonType="primary"
              onClick={handleSave}
              disabled={!isFormValid || isLoading}
              isLoading={isLoading}
              size="small"
            />
          </ModalFooter>
        </ModalOuterWrapper>
      </Modal>
      )}

      {showToaster && (
        <Toaster
          text={toasterMessage}
          type={toasterType}
          autoClose={3000}
          onCloseToast={() => setShowToaster(false)}
        />
      )}
    </>
  );
};

export default EditMacroModal;
